// Floating action bar shown while selection mode is active. Fixed to
// the bottom of the viewport on both mobile and desktop so it stays
// visible regardless of scroll position.

import { cn } from "../../lib/utils";
import {
  FolderInput,
  Pencil,
  Upload,
  Trash2,
} from "lucide-react";

interface Props {
  count: number;
  onMove: () => void;
  onRename: () => void;
  onExport: () => void;
  onDelete: () => void;
  onCancel: () => void;
  className?: string;
}

export function SelectionActionBar({
  count,
  onMove,
  onRename,
  onExport,
  onDelete,
  onCancel,
  className,
}: Props) {
  return (
    <div
      className={cn(
        "fixed inset-x-2 bottom-3 z-40 flex items-center gap-2 rounded-2xl border border-border bg-bg-raised px-3 py-2 shadow-2xl md:inset-x-auto md:bottom-4 md:left-1/2 md:w-fit md:-translate-x-1/2 md:rounded-lg",
        className,
      )}
      role="toolbar"
      aria-label="Selection actions"
    >
      <span className="text-sm font-semibold text-text">{count} selected</span>
      <button
        type="button"
        onClick={onCancel}
        className="ml-1 rounded px-2 py-1 text-xs text-text-soft hover:text-text"
      >
        Cancel
      </button>
      <div className="ml-auto flex items-center gap-1">
        <IconButton label="Move" onClick={onMove}>
          <FolderInput size={18} />
        </IconButton>
        <IconButton
          label="Rename"
          onClick={onRename}
          disabled={count !== 1}
        >
          <Pencil size={18} />
        </IconButton>
        <IconButton label="Export" onClick={onExport}>
          <Upload size={18} />
        </IconButton>
        <IconButton label="Delete" onClick={onDelete} danger>
          <Trash2 size={18} />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  children,
  label,
  onClick,
  disabled,
  danger,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "grid h-9 w-9 place-items-center rounded-lg text-text-soft hover:bg-bg-sidebar hover:text-text",
        disabled && "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-text-soft",
        danger && "hover:text-danger",
      )}
    >
      {children}
    </button>
  );
}
